import {action, computed, makeObservable, observable} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import Feature from "../../Feature";
import errorToast from "../../../components/toast/ErrorToast";
import successToast from "../../../components/toast/SuccessToast";
import LeadForm from "../../../domain/LeadForm";
import LeadDeliverySiteForm from "../../../domain/LeadDeliverySiteForm";
import FormSet from "../../../forms/fields/FormSet";

export default class CreateLeadFeature extends Feature {
    constructor(application) {
        super(application);
        this._form = null;
        this._leadDeliverySites = null;
        this._createdLead = null;
        this._isCreating = false;
        this._taxpayerIdentificationNumberValidation = null;
        this._possibleDuplicateLeads = null;
        this._selectedPossibleDuplicateLeadId = null;

        makeObservable(this, {
            _possibleDuplicateLeads: observable,
            _selectedPossibleDuplicateLeadId: observable,
            possibleDuplicateLeadRows: computed,
            selectedPossibleDuplicateLeadId: computed,
            hasPossibleDuplicateLeads: computed,
            canNotifyResponsible: computed,
            cancelPossibleDuplicateWarning: action,
            selectPossibleDuplicateLead: action,
            _warnAboutPossibleDuplicateLeads: action,
            _closePossibleDuplicateWarning: action,
            _form: observable,
            _leadDeliverySites: observable,
            _createdLead: observable,
            _isCreating: observable,
            _taxpayerIdentificationNumberValidation: observable,
            form: computed,
            leadDeliverySites: computed,
            createdLead: computed,
            createdLeadStageLabel: computed,
            createdLeadSuccessMessage: computed,
            belongsToAnActiveCustomer: computed,
            commercialChannelLabel: computed,
            isCreating: computed,
            shouldAskFranchiseBrandName: computed,
            shouldAskFiscalData: computed,
            shouldAskCommerceTypeSpecification: computed,
            shouldAskReferralObservation: computed,
            shouldAskLeadDeliverySites: computed,
            canCreateLead: computed,
            hasValidatedCurrentTaxpayerIdentificationNumber: computed,
            hasKnownBusinessName: computed,
            customerStatusWarning: computed,
            knownBusinessName: computed,
            shouldAskBusinessName: computed,
            lacksActiveRegistrationCertificate: computed,
            validateTaxpayerIdentificationNumber: action,
            _storeTaxpayerIdentificationNumberValidation: action,
            addLeadDeliverySite: action,
            removeLeadDeliverySite: action,
            _startNewLead: action,
            _startCreating: action,
            _finishCreating: action,
            _storeCreatedLead: action,
        });
    }

    load() {
        this._startNewLead();
        this.finishLoading();
    }

    get form() {
        return this._form;
    }

    get leadDeliverySites() {
        return this._leadDeliverySites;
    }

    get createdLead() {
        return this._createdLead;
    }

    get createdLeadStageLabel() {
        return this._stageLabelOf(this._createdLead.stage());
    }

    get hasPossibleDuplicateLeads() {
        return this._possibleDuplicateLeads !== null;
    }

    get selectedPossibleDuplicateLeadId() {
        return this._selectedPossibleDuplicateLeadId;
    }

    get canNotifyResponsible() {
        return this._selectedPossibleDuplicateLeadId !== null;
    }

    get possibleDuplicateLeadRows() {
        const labelsByReason = {
            TAXPAYER_IDENTIFICATION_NUMBER: 'CUIT',
            NAME: 'nombre',
            ADDRESS: 'domicilio',
        };
        return this._possibleDuplicateLeads.map((possibleDuplicateLead) => ({
            id: possibleDuplicateLead.id,
            fantasyName: possibleDuplicateLead.fantasyName,
            businessName: possibleDuplicateLead.businessName,
            stageLabel: this._stageLabelOf(possibleDuplicateLead.stage),
            commercialChannel: possibleDuplicateLead.commercialChannel,
            responsible: possibleDuplicateLead.responsible ?? 'Sin asignar',
            creator: possibleDuplicateLead.creator,
            matchedBy: possibleDuplicateLead.matchedBy.map((reason) => labelsByReason[reason]).join(', '),
        }));
    }

    _stageLabelOf(stageName) {
        const labelsByStageName = {
            UNASSIGNED: 'Sin asignar',
            NEW: 'Nuevo',
            CONTACTED: 'Contactado',
            NEGOTIATION: 'Negociación',
            PRODUCT_TRIAL: 'Prueba de producto',
            REGISTRATION_REQUEST: 'Solicitud de alta',
            STAND_BY: 'En espera',
            REGISTERED: 'Registrado',
            CURRENT_CUSTOMER: 'Cliente actual',
            CLOSED: 'Cerrado',
            NOT_APPLICABLE: 'No aplica',
        };
        return labelsByStageName[stageName];
    }

    get createdLeadSuccessMessage() {
        if (this._createdLead.isOfACurrentCustomer()) {
            return `Lead cerrado como cliente actual. Mensaje enviado por Teams al jefe de ${this._createdLead.commercialChannel()}`;
        }
        return `Lead creado en etapa: ${this.createdLeadStageLabel}`;
    }

    get isCreating() {
        return this._isCreating;
    }

    get shouldAskFranchiseBrandName() {
        return this._form.getFieldValue('hasFranchise') === this.yesAnswer();
    }

    get shouldAskFiscalData() {
        return this._form.getFieldValue('hasTaxpayerIdentificationNumber') === this.yesAnswer();
    }

    get shouldAskCommerceTypeSpecification() {
        return this._form.getFieldValue('commerceType') === this.otherCommerceType();
    }

    get shouldAskReferralObservation() {
        return this._form.getFieldValue('howTheyKnewUs') === this.otherReferralSource();
    }

    get shouldAskLeadDeliverySites() {
        return this._form.getFieldValue('hasPremises') === this.yesAnswer();
    }

    get canCreateLead() {
        const leadDeliverySitesAreValid = !this.shouldAskLeadDeliverySites
            || (this._leadDeliverySites.hasForms() && this._leadDeliverySites.isValid);
        const fiscalDataIsComplete = !this.shouldAskFiscalData || (
            this.hasValidatedCurrentTaxpayerIdentificationNumber
            && this._businessNameToCreate() !== ''
        );
        return this._form.isValid && fiscalDataIsComplete && leadDeliverySitesAreValid && !this._isCreating;
    }

    get hasValidatedCurrentTaxpayerIdentificationNumber() {
        return this._taxpayerIdentificationNumberValidation !== null
            && this._taxpayerIdentificationNumberValidation.taxpayerIdentificationNumber()
            === this._form.getFieldValue('taxpayerIdentificationNumber');
    }

    get belongsToAnActiveCustomer() {
        return this.hasValidatedCurrentTaxpayerIdentificationNumber
            && this._taxpayerIdentificationNumberValidation.belongsToAnActiveCustomer();
    }

    get commercialChannelLabel() {
        return this.belongsToAnActiveCustomer ? 'Notificar al jefe de canal' : 'Canal comercial';
    }

    get hasKnownBusinessName() {
        return this.hasValidatedCurrentTaxpayerIdentificationNumber && this.knownBusinessName !== '';
    }

    get shouldAskBusinessName() {
        return this.hasValidatedCurrentTaxpayerIdentificationNumber && this.knownBusinessName === '';
    }

    get lacksActiveRegistrationCertificate() {
        const isFoundInArca = this.hasValidatedCurrentTaxpayerIdentificationNumber
            && this._taxpayerIdentificationNumberValidation.isFoundInArca();
        return isFoundInArca && !this._taxpayerIdentificationNumberValidation.isActiveInArca();
    }

    get customerStatusWarning() {
        const warningsByCustomerStatus = {
            ACTIVE_CUSTOMER: 'El CUIT pertenece a un cliente activo de Sapore. Al crear el lead se cierra como cliente actual y se notifica al jefe del canal',
            DISABLED_CUSTOMER: 'El CUIT pertenece a un cliente inhabilitado de Sapore',
            NEW_CUSTOMER: 'Se daría de alta como cliente nuevo',
        };
        return warningsByCustomerStatus[this._taxpayerIdentificationNumberValidation.customerStatus()];
    }

    get knownBusinessName() {
        return this._taxpayerIdentificationNumberValidation.businessName();
    }

    yesAnswer() {
        return 'Sí';
    }

    noAnswer() {
        return 'No';
    }

    yesNoAnswers() {
        return [this.yesAnswer(), this.noAnswer()];
    }

    otherCommerceType() {
        return 'Otro';
    }

    otherReferralSource() {
        return 'Otros';
    }

    commerceTypes() {
        return [
            'Restaurante',
            'Catering',
            'Heladería',
            'Dietética',
            'Cafetería',
            'Supermercado',
            'Hotel',
            'Almacén',
            'Bar-Cervecería',
            'Distribuidor',
            'Panadería',
            'Vinoteca',
            this.otherCommerceType(),
        ];
    }

    referralSources() {
        return [
            'Meta',
            'TikTok',
            'Google',
            'LinkedIn',
            'Referido',
            'Ploteo de camiones',
            'Feria - Trade Show',
            'EAS',
            this.otherReferralSource(),
        ];
    }

    interestProducts() {
        return ['Panes', 'Pastelería', 'Viennoiserie', 'Dulces', 'Otro'];
    }

    originChannels() {
        return ['WhatsApp', 'Mail', 'Teléfono', 'Otro'];
    }

    commercialChannels() {
        return ['Horeca', 'Retail', 'Cadenas'];
    }

    optionsFor(values) {
        return values.map((value) => ({label: value, value: value}));
    }

    addLeadDeliverySite() {
        this._leadDeliverySites.addForm(LeadDeliverySiteForm.newEmpty(this));
    }

    removeLeadDeliverySite(leadDeliverySiteForm) {
        leadDeliverySiteForm.dispose();
        this._leadDeliverySites.removeForm(leadDeliverySiteForm.formId());
    }

    lookUpDeliveryZoneOf(leadDeliverySiteForm, markerPosition) {
        const lookUpNumber = leadDeliverySiteForm.startDeliveryZoneLookUp();
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => leadDeliverySiteForm.storeDeliveryZoneOfLookUp(lookUpNumber, response),
            handlesError: () => errorToast('No se pudo consultar la zona de cobertura.'),
        });
        return this._application.apiClient().deliveryZoneForCoordinates(
            markerPosition.lat,
            markerPosition.lng,
            responseHandler
        );
    }

    async validateTaxpayerIdentificationNumber() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => this._storeTaxpayerIdentificationNumberValidation(response),
            handlesError: (response) => response.errors().forEach((error) => errorToast(this._errorMessageOf(error))),
        });
        return this._application.apiClient().leadTaxpayerIdentificationNumberValidation(
            this._form.getFieldValue('taxpayerIdentificationNumber'),
            responseHandler
        );
    }

    async createLead() {
        this._startCreating();
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => this._createLeadOrWarnAbout(response.possibleDuplicateLeads()),
            handlesError: (response) => {
                response.errors().forEach((error) => errorToast(this._errorMessageOf(error)));
                this._finishCreating();
            },
        });
        return this._application.apiClient().possibleDuplicateLeads(this._leadToCreate(), responseHandler);
    }

    async createLeadAnyway() {
        this._closePossibleDuplicateWarning();
        return this._createLead();
    }

    cancelPossibleDuplicateWarning() {
        this._closePossibleDuplicateWarning();
        this._finishCreating();
    }

    selectPossibleDuplicateLead(possibleDuplicateLeadId) {
        this._selectedPossibleDuplicateLeadId = possibleDuplicateLeadId;
    }

    async notifyResponsibleOfSelectedPossibleDuplicateLead() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                successToast(`Se avisó del posible duplicado de ${response.fantasyName()}. No se creó el lead.`);
                this._closePossibleDuplicateWarning();
                this._startNewLead();
                this._finishCreating();
            },
            handlesError: (response) => {
                response.errors().forEach((error) => errorToast(this._errorMessageOf(error)));
            },
        });
        return this._application.apiClient().markAndNotifyPossibleDuplicateLead(
            this._selectedPossibleDuplicateLeadId,
            responseHandler
        );
    }

    async _createLead() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._storeCreatedLead(response);
                this._startNewLead();
                this._finishCreating();
            },
            handlesError: (response) => {
                response.errors().forEach((error) => errorToast(this._errorMessageOf(error)));
                this._finishCreating();
            },
        });
        return this._application.apiClient().createLead(this._leadToCreate(), responseHandler);
    }

    _leadToCreate() {
        const lead = this._form.values;
        return {
            ...lead,
            taxpayerIdentificationNumber: this.shouldAskFiscalData ? lead.taxpayerIdentificationNumber : null,
            businessName: this.shouldAskFiscalData ? this._businessNameToCreate() : null,
            franchiseBrandName: this.shouldAskFranchiseBrandName ? lead.franchiseBrandName : '',
            commerceTypeSpecification: this.shouldAskCommerceTypeSpecification ? lead.commerceTypeSpecification : '',
            referralObservation: this.shouldAskReferralObservation ? lead.referralObservation : '',
            leadDeliverySites: this._leadDeliverySitesToCreate(),
        };
    }

    _businessNameToCreate() {
        if (this.hasKnownBusinessName) {
            return this.knownBusinessName;
        }
        return this._form.getFieldValue('businessName');
    }

    _leadDeliverySitesToCreate() {
        if (!this.shouldAskLeadDeliverySites) {
            return [];
        }
        return this._leadDeliverySites.forms.map((leadDeliverySiteForm) => leadDeliverySiteForm.leadDeliverySite());
    }

    _errorMessageOf(error) {
        const messagesByErrorCode = {
            missing_lead_referral_observation: 'La observación es obligatoria cuando el lead nos conoció por Otros',
            incomplete_lead_legal_entity: 'El CUIT y la razón social van juntos',
            arca_unavailable: 'ARCA no responde, intentá validar el CUIT más tarde',
        };
        return messagesByErrorCode[error.code] ?? error.text;
    }

    _startNewLead() {
        this._leadDeliverySites?.forms.forEach((leadDeliverySiteForm) => leadDeliverySiteForm.dispose());
        this._leadDeliverySites = new FormSet([], this);
        this._form = LeadForm.newEmpty(this);
        this._taxpayerIdentificationNumberValidation = null;
    }

    _createLeadOrWarnAbout(possibleDuplicateLeads) {
        if (possibleDuplicateLeads.length === 0) {
            return this._createLead();
        }
        this._warnAboutPossibleDuplicateLeads(possibleDuplicateLeads);
    }

    _warnAboutPossibleDuplicateLeads(possibleDuplicateLeads) {
        this._possibleDuplicateLeads = possibleDuplicateLeads;
    }

    _closePossibleDuplicateWarning() {
        this._possibleDuplicateLeads = null;
        this._selectedPossibleDuplicateLeadId = null;
    }

    _startCreating() {
        this._createdLead = null;
        this._isCreating = true;
    }

    _finishCreating() {
        this._isCreating = false;
    }

    _storeTaxpayerIdentificationNumberValidation(validationResponse) {
        this._taxpayerIdentificationNumberValidation = validationResponse;
    }

    _storeCreatedLead(leadResponse) {
        this._createdLead = leadResponse;
    }
}
